import { useRouter } from 'next/router';
import { TUploadErrorCode } from 'preflight-core';
import { omit } from 'remeda';

import { Journal } from './journal';
import { UploadPayload } from './upload-file';
import { uploadInfo } from './upload-info';

export function useUploadRedirect({
  publisherId,
  journalId,
  editorialDesk = false,
  selectedArticleTypeId,
  selectedAddonId,
  isAddonEnabledV3 = false,
}: {
  journalId: Journal['slug'];
  publisherId: Journal['publisher']['slug'];
  editorialDesk?: boolean;
  selectedArticleTypeId?: number;
  selectedAddonId?: string;
  isAddonEnabledV3?: boolean;
}): {
  redirectToDocumentPage: (
    uploadPayload: UploadPayload,
    isContinueWithPdf?: boolean,
    setUploadStateContinuePdf?: (data: {
      name: string;
      error?: { json: { errorCode: TUploadErrorCode; errorReason: string } };
      data?: { json: object };
    }) => void,
  ) => void;
} {
  const router = useRouter();

  return {
    redirectToDocumentPage: (
      uploadPayload,
      isContinueWithPdf = false,
      setUploadStateContinuePdf = () => {},
    ) => {
      const queryEntries = Object.entries(
        omit(router.query, ['publisherSlug', 'journalSlug']),
      ) as [string, string][];
      const urlSearchParams = new URLSearchParams(queryEntries);

      if (uploadPayload.hasMacros) {
        urlSearchParams.set('hasMacros', 'true');
      }

      if (selectedAddonId) {
        urlSearchParams.set('addonId', selectedAddonId);
      }

      const urlSearchParamString =
        urlSearchParams.toString().length > 0
          ? `?${urlSearchParams.toString()}`
          : '';

      const otherParamsExist = urlSearchParams.has('hasMacros');
      const urlSearchParamEditorial = otherParamsExist
        ? `?${urlSearchParams.toString()}`
        : '';

      let urlToJump = '';

      if (isContinueWithPdf) {
        const apiUrl = `${process.env.NEXT_PUBLIC_API_BASE_URL}/publisher/${publisherId}/journal/${journalId}/resume-pdf-upload?documentId=${uploadPayload.documentId}`;

        fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            documentId: uploadPayload.documentId,
            selectedArticleTypeId,
          }),
        })
          .then(async (response) => {
            const res = await response.json();
            if (response.ok) {
              setUploadStateContinuePdf({
                name: 'success',
                data: { json: res },
              });

              const versionPath = isAddonEnabledV3 ? '/v3' : '';
              urlToJump = `/partner/${publisherId}/${journalId}/d${versionPath}/${uploadPayload.documentId}${urlSearchParamString}`;

              uploadInfo({
                documentId: uploadPayload.documentId,
                url: urlToJump,
              });
              window.location.href = urlToJump;
            } else {
              console.error('API request failed');
              setUploadStateContinuePdf({
                name: 'error',
                error: { json: res },
              });
            }
          })
          .catch((error) => {
            setUploadStateContinuePdf({
              name: 'error',
              error: { json: error },
            });
            console.error('Error in API request:', error);
          });
      } else {
        const versionPath = isAddonEnabledV3 ? '/v3' : '';

        urlToJump = editorialDesk
          ? `/editorial-desk/partner/${publisherId}/${journalId}/d/${uploadPayload.documentId}${urlSearchParamEditorial}`
          : `/partner/${publisherId}/${journalId}/d${versionPath}/${uploadPayload.documentId}${urlSearchParamString}`;

        uploadInfo({
          documentId: uploadPayload.documentId,
          url: `/${router.locale}${urlToJump}`,
        });
        window.location.href = urlToJump;
      }
    },
  };
}
